/**
 * Session Manager
 *
 * Session invalidation flag, single-flight token refresh,
 * and authentication-lost handling.
 */

import { apiLogger } from '@/core/utils/logger';
import { setSessionStatus } from '@/core/auth/sessionStatus';
import { fetchFreshCsrfToken } from './csrf';
import { extractErrorMessage } from '@/shared/utils/error';

// SESSION CONVERGENCE FLAG
// Once set, all further requests immediately return 401 (no fetches, no retries).
// Prevents request pile-up during redirect after auth loss.

let sessionInvalidated = false;
let sessionInvalidationTimer: ReturnType<typeof setTimeout> | null = null;

/** How long sessionInvalidated stays true before auto-resetting (ms).
 *  If the redirect to /login succeeds within this window, the page reloads
 *  and the flag doesn't matter. If the redirect fails (e.g. service-worker
 *  intercepts, popup blocker, etc.), requests resume instead of being
 *  permanently blocked. */
const SESSION_INVALIDATION_TTL = 10_000;

/** Mark the session as invalidated — prevents further REST requests for up to SESSION_INVALIDATION_TTL ms */
export function markSessionInvalidated(): void {
  sessionInvalidated = true;
  apiLogger.info('[baseQueryWithRetry] Session marked as invalidated — blocking further requests');

  // Auto-reset after TTL so the app isn't permanently bricked if redirect fails
  if (sessionInvalidationTimer) clearTimeout(sessionInvalidationTimer);
  sessionInvalidationTimer = setTimeout(() => {
    sessionInvalidationTimer = null;
    if (sessionInvalidated) {
      sessionInvalidated = false;
      apiLogger.info('[baseQueryWithRetry] Session invalidation auto-reset after timeout — requests unblocked');
    }
  }, SESSION_INVALIDATION_TTL);
}

/** Check if the session is currently invalidated */
export function isSessionInvalidated(): boolean {
  return sessionInvalidated;
}

/** Reset session invalidation — used by recovery code (e.g. tab visibility handler) */
export function resetSessionInvalidated(): void {
  if (!sessionInvalidated) return;
  sessionInvalidated = false;
  if (sessionInvalidationTimer) {
    clearTimeout(sessionInvalidationTimer);
    sessionInvalidationTimer = null;
  }
  apiLogger.info('[baseQueryWithRetry] Session invalidation manually reset — requests unblocked');
}

// SINGLE-FLIGHT TOKEN REFRESH MECHANISM

/** Result of a token refresh attempt */
export interface RefreshResult {
  success: boolean;
  /** true if the failure was transient (5xx, network error) and session should survive */
  transient: boolean;
}

/**
 * Module-level refresh promise to prevent refresh stampede.
 * When multiple requests get 401s simultaneously, only one refresh is triggered.
 * All other requests wait for this promise to resolve before retrying.
 */
let singleFlightRefreshPromise: Promise<RefreshResult> | null = null;

/**
 * Endpoints that should NOT trigger refresh attempts (they are part of the auth flow)
 */
const AUTH_FLOW_ENDPOINTS = [
  '/auth/login',
  '/auth/logout',
  '/auth/refresh',
  '/auth/validate',
  '/auth/jwt/refresh',
  '/auth/csrf-token',
  '/auth/otp/send',
  '/auth/otp/verify',
];

/**
 * Check if an endpoint is part of the auth flow and should skip 401 refresh handling
 */
export function isAuthFlowEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return AUTH_FLOW_ENDPOINTS.some(endpoint => url.includes(endpoint));
}

/**
 * Classify whether a refresh failure is transient (session should survive)
 * or permanent (session should be killed).
 */
function isTransientError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { status?: number; name?: string; message?: string; code?: string };
  if (typeof err.status === 'number' && err.status >= 500) return true;
  if (err.name === 'AbortError' || err.name === 'TimeoutError') return true;
  if (err.code === 'FETCH_ERROR' || err.name === 'TypeError') return true;
  const msg = (err.message || '').toLowerCase();
  if (msg.includes('fetch') || msg.includes('network') || msg.includes('timeout') || msg.includes('abort')) return true;
  return false;
}

/**
 * Attempt a single-flight token refresh.
 * Returns a RefreshResult indicating success/failure and whether the failure was transient.
 * Uses module-level promise to ensure only one refresh happens at a time.
 */
export async function attemptSingleFlightRefresh(): Promise<RefreshResult> {
  // If refresh is already in progress, wait for it
  if (singleFlightRefreshPromise) {
    apiLogger.info('[SingleFlightRefresh] Refresh already in progress, waiting...');
    return singleFlightRefreshPromise;
  }

  // Start a new refresh
  apiLogger.info('[SingleFlightRefresh] Starting new token refresh');

  singleFlightRefreshPromise = (async () => {
    try {
      // Dynamically import to avoid circular dependency
      const { secureAuthService } = await import('@/core/auth/services');
      await secureAuthService.refreshAccessToken();

      // Small delay to allow cookies to settle
      await new Promise(resolve => setTimeout(resolve, 100));

      // Preemptively refresh CSRF token after access token refresh.
      try {
        await fetchFreshCsrfToken();
        apiLogger.info('[SingleFlightRefresh] CSRF token also refreshed');
      } catch {
        apiLogger.warn('[SingleFlightRefresh] CSRF refresh failed (best-effort)');
      }

      // Best-effort WS token sync — keeps WebSocket in sync after REST refresh
      try {
        const { wsAuthService } = await import('@/core/auth/services/WebSocketAuthService');
        await wsAuthService.getToken({ forceRefresh: true });
        apiLogger.info('[SingleFlightRefresh] WS token also refreshed');
      } catch {
        apiLogger.warn('[SingleFlightRefresh] WS token refresh failed (non-critical)');
      }

      apiLogger.info('[SingleFlightRefresh] Token refresh successful');
      return { success: true, transient: false } as RefreshResult;
    } catch (error) {
      const transient = isTransientError(error);
      apiLogger.error('[SingleFlightRefresh] Token refresh failed', {
        message: extractErrorMessage(error, 'Token refresh failed'),
        transient,
      });
      return { success: false, transient } as RefreshResult;
    } finally {
      // Clear the promise so future refreshes can happen
      singleFlightRefreshPromise = null;
    }
  })();

  return singleFlightRefreshPromise;
}

/**
 * Handle authentication lost - redirect to login with return URL.
 * Idempotent: calling multiple times within the TTL window is safe.
 */
export function handleAuthenticationLost(): void {
  if (typeof window === 'undefined') return;

  // Idempotency guard — if already invalidated, don't fire toast/redirect again
  if (sessionInvalidated) {
    apiLogger.info('[handleAuthenticationLost] Already invalidated, skipping duplicate call');
    return;
  }

  // CRITICAL: Set sessionInvalidated synchronously BEFORE any async work.
  // This prevents other in-flight requests from piling up while redirect is pending.
  markSessionInvalidated();

  // Also halt WS reconnection immediately to prevent unnecessary token fetch requests.
  // Uses window.__socketClient to avoid circular dependency (sessionManager -> SocketClient -> auth).
  try {
    const client = window.__socketClient;
    if (client) client.sessionInvalidated = true;
  } catch { /* ignore */ }

  apiLogger.info('[SingleFlightRefresh] Authentication lost, showing session overlay');

  setSessionStatus('expired');

  // Fallback redirect — if the overlay's "Sign In" button isn't clicked within 5s,
  // force redirect to ensure the user eventually reaches /login.
  setTimeout(() => {
    try {
      import('@/core/auth/services').then(({ secureAuthService }) => {
        secureAuthService.handleAuthenticationLost();
      }).catch(() => {
        const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/login?returnUrl=${returnUrl}&error=session_expired`;
      });
    } catch {
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?returnUrl=${returnUrl}&error=session_expired`;
    }
  }, 5000);
}
